<template>
  <v-container>
    <!-- Page Header -->
    <v-row>
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center mb-4">
            <h1 class="text-h4 mb-0">{{ $t('wallets.title') }}</h1>
            <v-btn color="primary" @click="showAddDialog = true">{{ $t('wallets.add') }}</v-btn>
          </div>
      </v-col>
    </v-row>
    
    <!-- Filter Buttons Row (like transactions) -->
    <v-row class="period-buttons-row">
      <v-col cols="12" sm="6" md="4" lg="4" xl="4"></v-col>
      <v-col cols="12" sm="4" md="4" lg="4" xl="4">
        <v-btn-toggle v-model="walletTypeFilter" mandatory class="period-buttons">
          <v-btn value="all" size="small">{{ $t('wallets.all') }}</v-btn>
          <v-btn value="office" size="small">{{ $t('wallets.office') }}</v-btn>
          <v-btn value="exchange" size="small">{{ $t('wallets.exchanges') }}</v-btn>
        </v-btn-toggle>
      </v-col>
      <v-col cols="12" sm="6" md="4" lg="4" xl="4"></v-col>
    </v-row>
    
    <!-- Filters -->
    <v-row>
      <v-col cols="12">
        <v-card variant="tonal" class="pa-4">
          <v-card-title class="text-h6 mb-4">{{ $t('wallets.filters') }}</v-card-title>
          <v-row>
            <v-col cols="12" sm="6" md="2">
              <v-select
                v-model="filterExchange"
                :items="exchanges"
                item-title="name"
                item-value="id"
                :label="$t('wallets.exchange')"
                clearable
                density="compact"
              />
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-select
                v-model="filterCoin"
                :items="coins"
                item-title="symbol"
                item-value="id"
                :label="$t('wallets.coin')"
                clearable
                density="compact"
              />
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-card variant="outlined">
          <v-card-text>
            <v-data-table
              :headers="headers"
              :items="filteredWallets"
              :items-per-page="10"
              :items-per-page-options="[10, 25, 50, 100]"
              class="elevation-0"
            >
              <template #item.exchange_name="{ item }">
                {{ item.exchange?.name || '-' }}
              </template>
              <template #item.office_name="{ item }">
                {{ item.office ? $t('Yes') : '-' }}
              </template>
              <template #item.coin_symbol="{ item }">
                <router-link :to="`/wallets/${item.id}`" class="wallet-link">
                  <strong>{{ item.coin?.symbol || '-' }}</strong>
                </router-link>
              </template>
              <template #item.balance="{ item }">
                {{ Number(item.balance || 0).toLocaleString() }}
              </template>
              <template #item.display_name="{ item }">
                {{ item.display_name || '-' }}
              </template>
              <template #item.actions="{ item }">
                <v-btn variant="plain" @click="router.push(`/wallets/${item.id}`)" icon="mdi-eye" :title="$t('wallets.viewDetails')"></v-btn>
                <v-btn variant="plain" @click="editWallet(item)" icon="mdi-pencil"></v-btn>
                <v-btn variant="plain" color="error" @click="deleteWallet(item.id)" icon="mdi-delete"></v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
        <v-card class="mt-4">
          <v-card-text>
            <div class="d-flex justify-space-between align-center">
              <div>
                <strong>{{ $t('wallets.grandTotals') }}:</strong>
                {{ $t('wallets.balance') }}: {{ totalBalance.toLocaleString() }}
              </div>
              <div class="text-caption">
                {{ $t('wallets.totalWallets') }}: {{ filteredWallets.length }}
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-dialog v-model="showAddDialog" max-width="500" @update:model-value="(val) => { if (!val) resetForm(); }">
      <v-card>
        <v-card-title>{{ walletForm.id ? $t('wallets.edit') : $t('wallets.add') }}</v-card-title>
        <v-card-text>
          <v-form>
            <v-select
              v-model="walletForm.exchange_id"
              :items="exchanges"
              item-title="name"
              item-value="id"
              :label="$t('wallets.exchange')"
              clearable
            />
            <v-checkbox
              v-model="walletForm.office"
              :label="$t('wallets.isPhysicalOffice')"
            />
            <v-select
              v-model="walletForm.coin_id"
              :items="coins"
              item-title="name"
              item-value="id"
              :label="$t('wallets.coin')"
            />
            <v-text-field v-model="walletForm.name" :label="$t('wallets.name')" />
            <v-text-field v-model="walletForm.address" :label="$t('wallets.address')" />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showAddDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" @click="saveWallet">{{ $t('common.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { apiGet, apiPost, apiPut, apiDelete, getErrorMessage } from '../utils/api'
import { getDateRangeForPeriod } from '../utils/date'

const router = useRouter()
const { t } = useI18n()

interface Coin {
  id: number
  name: string
  symbol: string
}

interface Exchange {
  id: number
  name: string
  office?: boolean
}

interface Wallet {
  id: number
  exchange: {
    id: number
    name: string
    office?: boolean
  } | null
  office: boolean
  coin: Coin
  balance: number // Calculated balance (for display)
  display_name?: string
  exchange_name?: string // Flattened for table sorting
  office_name?: string // Flattened for table sorting
  coin_symbol?: string // Flattened for table sorting
  name?: string
  address?: string
  created_at?: string // Created timestamp for date filtering
}

const wallets = ref<Wallet[]>([])
const coins = ref<Coin[]>([])
const exchanges = ref<Exchange[]>([])
const filterExchange = ref<number | null>(null)
const walletTypeFilter = ref<'all' | 'office' | 'exchange'>('all')
const filterCoin = ref<number | null>(null)
const headers = computed(() => [
  { title: t('wallets.exchange'), key: 'exchange_name', sortable: true },
  { title: t('wallets.office'), key: 'office_name', sortable: true },
  { title: t('wallets.coin'), key: 'coin_symbol', sortable: true },
  { title: t('wallets.displayName'), key: 'display_name', sortable: true },
  { title: t('wallets.balance'), key: 'balance', sortable: true, align: 'end' as const },
  { title: t('common.actions'), key: 'actions', sortable: false },
])
const showAddDialog = ref(false)
const walletForm = ref({
  id: null as number | null,
  exchange_id: null as number | null,
  office: false as boolean,
  coin_id: null as number | null,
  name: '',
  address: ''
})

const loadExchanges = async () => {
  try {
    const response = await apiGet('/api/exchanges')
    if (response.ok) {
      exchanges.value = await response.json()
    }
  } catch (error) {
    console.error('Load exchanges error:', error)
  }
}

const loadCoins = async () => {
  try {
    const response = await apiGet('/api/coins')
    if (response.ok) {
      coins.value = await response.json()
    }
  } catch (error) {
    console.error('Load coins error:', error)
  }
}

const loadWallets = async () => {
  try {
    const response = await apiGet('/api/wallets')
    if (response.ok) {
      const walletData = await response.json()
      
      // Ensure all wallets have the required structure with proper nested objects
      // Handle case where API might return empty array or malformed data
      if (!Array.isArray(walletData) || walletData.length === 0) {
        console.error('Invalid wallet data received:', walletData)
        wallets.value = []
        return
      }
      
      wallets.value = walletData.map((w: any, index: number) => {
        // Skip if wallet object is completely null/undefined
        if (!w || typeof w !== 'object') {
          return null
        }
        
        // Extract exchange - handle null or object
        let exchange: { id: number; name: string; office?: boolean } | null = null
        if (w.exchange) {
          if (typeof w.exchange === 'object' && !Array.isArray(w.exchange) && w.exchange !== null) {
            const exchangeId = w.exchange.id
            if (exchangeId !== null && exchangeId !== undefined && exchangeId !== 0) {
              exchange = {
                id: Number(exchangeId),
                name: String(w.exchange.name || ''),
                office: w.exchange.office === true || w.exchange.office === 'true' || w.exchange.office === 1
              }
            }
          }
        }
        
        // Extract office - use exchange.office if exchange exists, otherwise use wallet.office
        const office: boolean = exchange?.office === true || (exchange === null && (w.office === true || w.office === 'true' || w.office === 1))
        
        // Extract coin - always an object (required field)
        let coin: { id: number; name: string; symbol: string } = { id: 0, name: '', symbol: '' }
        if (w.coin) {
          if (typeof w.coin === 'object' && !Array.isArray(w.coin) && w.coin !== null) {
            const coinId = w.coin.id
            if (coinId !== null && coinId !== undefined && coinId !== 0) {
              coin = {
                id: Number(coinId),
                name: String(w.coin.name || ''),
                symbol: String(w.coin.symbol || '')
              }
            }
          }
        }
        
        const mappedWallet: Wallet = {
          id: Number(w.id),
          exchange: exchange,
          office: office,
          coin: coin,
          exchange_name: exchange ? exchange.name : '-',
          office_name: office ? t('yes') : '-',
          coin_symbol: coin.symbol || '-',
          balance: w.balance !== null && w.balance !== undefined ? Number(w.balance) : 0,
          display_name: w.display_name || undefined,
          name: w.name || undefined,
          address: w.address || undefined,
          created_at: w.created_at || undefined
        }
        
        return mappedWallet
      }).filter((w) => w !== null) as Wallet[] // Remove any null entries from invalid wallets
    } else {
      console.error('Failed to load wallets:', response.status)
    }
  } catch (error) {
    console.error('Load wallets error:', error)
  }
}

const saveWallet = async () => {
  try {
    const url = walletForm.value.id ? `/api/wallets/${walletForm.value.id}` : '/api/wallets'
    const body = { 
      wallet: { 
        exchange_id: walletForm.value.exchange_id, 
        office: walletForm.value.office,
        coin_id: walletForm.value.coin_id, 
        name: walletForm.value.name,
        address: walletForm.value.address
      } 
    }
    
    const response = walletForm.value.id 
      ? await apiPut(url, body)
      : await apiPost(url, body)
    
    if (response.ok) {
      showAddDialog.value = false
      loadWallets()
      resetForm()
    } else {
      const errors = await getErrorMessage(response, t('errors.unknownError'))
      alert(t('wallets.failedToSave', { errors }))
    }
  } catch (error) {
    console.error('Save wallet error:', error)
    alert(t('wallets.errorSaving'))
  }
}

const editWallet = (wallet: Wallet) => {
  walletForm.value = {
    id: wallet.id,
    exchange_id: wallet.exchange?.id || null,
    office: wallet.office || false,
    coin_id: wallet.coin?.id || null,
    name: wallet.name || '',
    address: wallet.address || ''
  }
  showAddDialog.value = true
}

const deleteWallet = async (id: number) => {
  if (!confirm(t('wallets.areYouSure'))) return
  try {
    const response = await apiDelete(`/api/wallets/${id}`)
    if (response.ok) {
      loadWallets()
    }
  } catch (error) {
    console.error('Delete wallet error:', error)
  }
}

const resetForm = () => {
  walletForm.value = { id: null, exchange_id: null, office: false, coin_id: null, name: '', address: '' }
}

onMounted(() => {
  loadCoins()
  loadExchanges()
  loadWallets()
})

const filteredWallets = computed(() => {
  return wallets.value.filter(w => {
    // Exchange filter
    const byExchange = filterExchange.value ? (w.exchange?.id === filterExchange.value) : true
    
    // Wallet type filter (all, office, exchange)
    // Office wallet: wallet.office === true OR exchange.office === true
    const isOfficeWallet = w.office === true || w.exchange?.office === true
    
    let byWalletType = true
    if (walletTypeFilter.value === 'office') {
      byWalletType = isOfficeWallet
    } else if (walletTypeFilter.value === 'exchange') {
      byWalletType = !isOfficeWallet && w.exchange !== null
    } else {
      // 'all' - show everything
      byWalletType = true
    }
    
    // Coin filter
    const byCoin = filterCoin.value ? (w.coin?.id === filterCoin.value) : true
    
    return byExchange && byWalletType && byCoin
  })
})

const totalBalance = computed(() => filteredWallets.value.reduce((sum, w) => sum + (Number(w.balance) || 0), 0))
</script>

<style scoped>
.wallet-link {
  text-decoration: none;
  color: inherit;
  cursor: pointer;
}

.wallet-link:hover {
  color: rgb(var(--v-theme-primary));
  text-decoration: underline;
}

.period-buttons-row {
  margin-top: -38px;
  margin-bottom: 16px;
}

.period-buttons :deep(.v-btn) {
  margin: 0;
  padding: 4px 12px;
  min-width: auto;
  height: 24px;
  max-height: 24px;
}

.period-buttons :deep(.v-btn-toggle__content) {
  gap: 0;
}
</style>

